import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { environment } from '@customer-portal/environments';

import type { FeatureAnnouncement, FeatureInteractions } from '../models';
import { FeatureAnnouncementManifestService } from '../services/feature-announcement-manifest.service';
import { FeatureInteractionStorageService } from '../services/feature-interaction-storage.service';
import {
  featureInteractionsEqual,
  getActiveFeatureAnnouncements,
  getFeatureAnnouncementRetentionLowerBound,
  isFeatureAnnouncementWithinRetentionWindow,
  parseFeatureAnnouncementManifest,
  pruneFeatureInteractions,
} from '../utils/feature-announcement.utils';
import {
  LoadFeatureAnnouncements,
  LoadFeatureAnnouncementsFail,
  LoadFeatureAnnouncementsSuccess,
  MarkAllFeatureAnnouncementsSeen,
  MarkFeatureAnnouncementSeen,
} from './actions';

export const FEATURE_ANNOUNCEMENT_LOAD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  PARTIAL: 'partial',
  ERROR: 'error',
} as const;

export type FeatureAnnouncementLoadStatus =
  (typeof FEATURE_ANNOUNCEMENT_LOAD_STATUS)[keyof typeof FEATURE_ANNOUNCEMENT_LOAD_STATUS];

export interface FeatureAnnouncementStateModel {
  features: FeatureAnnouncement[];
  interactions: FeatureInteractions;
  status: FeatureAnnouncementLoadStatus;
  now: number;
  retentionMonths: number;
}

function getRetentionMonths(): number {
  const configuredMonths = environment.featureAnnouncementRetentionMonths;

  return Number.isInteger(configuredMonths) && configuredMonths > 0
    ? configuredMonths
    : 1;
}

const defaultState: FeatureAnnouncementStateModel = {
  features: [],
  interactions: {},
  status: FEATURE_ANNOUNCEMENT_LOAD_STATUS.IDLE,
  now: Date.now(),
  retentionMonths: getRetentionMonths(),
};

@State<FeatureAnnouncementStateModel>({
  name: 'featureAnnouncements',
  defaults: defaultState,
})
@Injectable()
export class FeatureAnnouncementState {
  private readonly document = inject(DOCUMENT);
  private readonly manifestService = inject(FeatureAnnouncementManifestService);
  private readonly interactionStorage = inject(
    FeatureInteractionStorageService,
  );

  @Action(LoadFeatureAnnouncements)
  loadFeatureAnnouncements(
    ctx: StateContext<FeatureAnnouncementStateModel>,
  ): Observable<void> {
    const { status } = ctx.getState();

    if (
      status === FEATURE_ANNOUNCEMENT_LOAD_STATUS.READY ||
      status === FEATURE_ANNOUNCEMENT_LOAD_STATUS.LOADING
    ) {
      return of(undefined);
    }

    ctx.patchState({ status: FEATURE_ANNOUNCEMENT_LOAD_STATUS.LOADING });

    return this.manifestService.getManifest().pipe(
      map((manifest) =>
        parseFeatureAnnouncementManifest(manifest, this.document),
      ),
      tap(({ features, authoritative }) =>
        ctx.dispatch(
          new LoadFeatureAnnouncementsSuccess(features, authoritative),
        ),
      ),
      map(() => undefined),
      catchError(() => ctx.dispatch(new LoadFeatureAnnouncementsFail())),
    );
  }

  @Action(LoadFeatureAnnouncementsSuccess)
  loadFeatureAnnouncementsSuccess(
    ctx: StateContext<FeatureAnnouncementStateModel>,
    { features, authoritative }: LoadFeatureAnnouncementsSuccess,
  ): void {
    const now = Date.now();
    const retentionMonths = getRetentionMonths();
    const storedInteractions = authoritative
      ? this.interactionStorage.loadFeatureInteractionsFromLocalStorage()
      : ctx.getState().interactions;
    const interactions = authoritative
      ? pruneFeatureInteractions(
          storedInteractions,
          features,
          now,
          retentionMonths,
        )
      : storedInteractions;

    ctx.patchState({
      features,
      interactions,
      status: authoritative
        ? FEATURE_ANNOUNCEMENT_LOAD_STATUS.READY
        : FEATURE_ANNOUNCEMENT_LOAD_STATUS.PARTIAL,
      now,
      retentionMonths,
    });

    if (
      authoritative &&
      !featureInteractionsEqual(storedInteractions, interactions)
    ) {
      this.interactionStorage.saveFeatureInteractionsToLocalStorage(
        interactions,
      );
    }
  }

  @Action(LoadFeatureAnnouncementsFail)
  loadFeatureAnnouncementsFail(
    ctx: StateContext<FeatureAnnouncementStateModel>,
  ): void {
    ctx.patchState({ status: FEATURE_ANNOUNCEMENT_LOAD_STATUS.ERROR });
  }

  @Action(MarkFeatureAnnouncementSeen)
  markFeatureAnnouncementSeen(
    ctx: StateContext<FeatureAnnouncementStateModel>,
    { featureKey, tourCompleted }: MarkFeatureAnnouncementSeen,
  ): void {
    const state = ctx.getState();
    const now = Date.now();

    if (
      state.status !== FEATURE_ANNOUNCEMENT_LOAD_STATUS.READY ||
      !featureKey.trim()
    ) {
      return;
    }
    ctx.patchState({ now });

    const feature = state.features.find(
      (announcement) => announcement.featureKey === featureKey,
    );
    const lowerBound = getFeatureAnnouncementRetentionLowerBound(
      now,
      state.retentionMonths,
    );

    if (
      !feature ||
      !isFeatureAnnouncementWithinRetentionWindow(feature, now, lowerBound)
    ) {
      return;
    }

    const previous = Object.prototype.hasOwnProperty.call(
      state.interactions,
      featureKey,
    )
      ? state.interactions[featureKey]
      : undefined;
    const interactions = Object.fromEntries([
      ...Object.entries(state.interactions),
      [
        featureKey,
        {
          seenAt: new Date(now).toISOString(),
          tourCompleted: tourCompleted || previous?.tourCompleted === true,
        },
      ],
    ]);

    this.persistFeatureInteractions(ctx, interactions, now);
  }

  @Action(MarkAllFeatureAnnouncementsSeen)
  markAllFeatureAnnouncementsSeen(
    ctx: StateContext<FeatureAnnouncementStateModel>,
  ): void {
    const state = ctx.getState();
    const now = Date.now();

    if (state.status !== FEATURE_ANNOUNCEMENT_LOAD_STATUS.READY) {
      return;
    }
    ctx.patchState({ now });

    const newlySeen = getActiveFeatureAnnouncements(
      state.features,
      now,
      state.retentionMonths,
    )
      .filter(
        (feature) =>
          !Object.prototype.hasOwnProperty.call(
            state.interactions,
            feature.featureKey,
          ),
      )
      .map((feature) => [
        feature.featureKey,
        { seenAt: new Date(now).toISOString(), tourCompleted: false },
      ]);

    this.persistFeatureInteractions(
      ctx,
      Object.fromEntries([...Object.entries(state.interactions), ...newlySeen]),
      now,
    );
  }

  private persistFeatureInteractions(
    ctx: StateContext<FeatureAnnouncementStateModel>,
    interactions: FeatureInteractions,
    now: number,
  ): void {
    const state = ctx.getState();
    const pruned = pruneFeatureInteractions(
      interactions,
      state.features,
      now,
      state.retentionMonths,
    );

    ctx.patchState({ interactions: pruned });

    if (!featureInteractionsEqual(state.interactions, pruned)) {
      this.interactionStorage.saveFeatureInteractionsToLocalStorage(pruned);
    }
  }
}
